// Data hooks for the sky widgets. Same pattern as src/data/queries.ts:
// one shared TanStack query per key, seeded from a localStorage snapshot
// that always counts as stale until the server has answered once.

import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import type { IssTlePayload, QuakesPayload, SpaceWeatherPayload, UvPayload } from "../../../shared/sky/types";
import { useHousehold } from "../../app/useHousehold";
import { api } from "../../data/api";
import { readSnapshot, writeSnapshot } from "../../data/persist";

const MIN = 60_000;

export function useSkyQuery<T>(key: readonly unknown[], path: string, opts: { refetchMs: number; staleMs: number; enabled?: boolean }) {
  const snapKey = JSON.stringify(key);
  const q = useQuery({
    queryKey: key,
    queryFn: ({ signal }) => api<T>(path, { signal }),
    initialData: () => readSnapshot<T>(snapKey)?.data,
    initialDataUpdatedAt: () => readSnapshot<T>(snapKey)?.savedAt,
    staleTime: (query) => (query.state.dataUpdateCount === 0 ? 0 : opts.staleMs),
    refetchInterval: opts.refetchMs,
    enabled: opts.enabled ?? true,
  });
  useEffect(() => {
    if (q.data !== undefined && q.dataUpdatedAt && q.status === "success" && q.fetchStatus === "idle") writeSnapshot(snapKey, q.data, q.dataUpdatedAt);
  }, [q.data, q.dataUpdatedAt, q.status, q.fetchStatus, snapKey]);
  return q;
}

/** Planetary Kp: worker caches 30 min. */
export function useSpaceWeather() {
  return useSkyQuery<SpaceWeatherPayload>(["sky", "space-weather"], "/sky/space-weather", { refetchMs: 30 * MIN, staleMs: 15 * MIN });
}

export function useQuakes(radiusKm: number, minMag: number) {
  const { settings } = useHousehold();
  const loc = settings.location;
  return useSkyQuery<QuakesPayload>(["sky", "quakes", loc?.lat, loc?.lon, radiusKm, minMag], `/sky/quakes?radiusKm=${radiusKm}&minMag=${minMag}`, {
    refetchMs: 10 * MIN,
    staleMs: 5 * MIN,
    enabled: Boolean(loc),
  });
}

export function useUv() {
  const { settings } = useHousehold();
  const loc = settings.location;
  return useSkyQuery<UvPayload>(["sky", "uv", loc?.label ?? ""], "/sky/uv", { refetchMs: 60 * MIN, staleMs: 30 * MIN, enabled: Boolean(loc) });
}

/** ISS elements change a few times a day; the worker caches 6 h. */
export function useIssTle(enabled: boolean) {
  return useSkyQuery<IssTlePayload>(["sky", "iss-tle"], "/sky/iss-tle", { refetchMs: 3 * 60 * MIN, staleMs: 60 * MIN, enabled });
}
